import EventEmitter2 from 'eventemitter2';
import { createParser, type EventSourceParser } from 'eventsource-parser';

import { AutoReconnectConnection, NonRetryableConnectionError } from './connection';

/** 재시도해도 같은 결과인 인증/인가 실패 — 새 토큰 없이는 영구히 같은 응답 */
const NON_RETRYABLE_STATUSES = new Set([401, 403]);

/**
 * SSE 트랜스포트 — fetch + ReadableStream + eventsource-parser (lobehub/Vercel 방식).
 * native EventSource와 달리 헤더를 실을 수 있어 `Authorization: Bearer` 로 인증한다.
 * AFFiNE SocketConnection 자리의 SSE판. shareId(=url)로 URL당 연결 1개(refCount).
 *
 * - inner = AbortController (doDisconnect 시 abort로 fetch/reader 중단)
 * - 파싱된 message data(문자열)는 자체 emitter로 onMessage 구독자에게 전달
 *   (재연결로 fetch가 새로 열려도 구독은 유지 — 소비측은 재부착 불필요)
 */
export class SSEConnection extends AutoReconnectConnection<AbortController> {
  private readonly messageEmitter = new EventEmitter2();
  // BE가 정상 종료 시 보내는 named `end` sentinel 수신 여부. true면 스트림 종료를 "정상 완료"로
  // 처리해 재연결하지 않는다(네트워크 끊김과 구분). 매 doConnect 시작 시 초기화.
  private gracefullyEnded = false;

  constructor(
    private readonly url: string,
    private readonly getToken: () => string,
  ) {
    super();
  }

  override get shareId(): string {
    return this.url;
  }

  /** 파싱된 SSE data(문자열) 구독. 반환값 호출로 해제. */
  onMessage(cb: (data: string) => void): () => void {
    this.messageEmitter.on('message', cb);
    return () => {
      this.messageEmitter.off('message', cb);
    };
  }

  override async doConnect(signal?: AbortSignal): Promise<AbortController> {
    this.gracefullyEnded = false; // 새 연결 시도마다 초기화
    const controller = new AbortController();
    // 연결 취소(AutoReconnect connectingAbort)/상위 abort 시 fetch도 중단
    signal?.addEventListener('abort', () => controller.abort(), { once: true });

    const token = this.getToken();
    const res = await fetch(this.url, {
      method: 'GET',
      headers: {
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      signal: controller.signal,
    });
    if (!res.ok || !res.body) {
      controller.abort();
      // 401/403 = 만료·무효 토큰 → 같은 토큰으로 재연결해도 영구히 같은 응답이므로 즉시 포기
      // (방치된 탭이 3초마다 403을 찍던 원인). 그 외 상태코드는 일시 장애로 보고 재연결 대상.
      if (NON_RETRYABLE_STATUSES.has(res.status)) {
        throw new NonRetryableConnectionError(`SSE connect failed: ${res.status}`, res.status);
      }
      throw new Error(`SSE connect failed: ${res.status}`);
    }

    // 백그라운드 펌프 (connected 판정 = response 수신 시점)
    void this.pump(res.body, controller);
    return controller;
  }

  private async pump(body: ReadableStream<Uint8Array>, controller: AbortController) {
    const parser: EventSourceParser = createParser({
      onEvent: (event) => {
        // BE 정상 종료 sentinel(named `end`) → 재연결 금지 플래그. 스트림 종료가 곧 뒤따른다.
        // (transport 레벨에서 "정상 완료 vs 끊김"을 판정 — payload 파싱에 의존하지 않음)
        if (event.event === 'end') {
          this.gracefullyEnded = true;
          return;
        }
        // ping 등 named 이벤트 무시 (진행 delta/스냅샷은 default 'message')
        if (event.event && event.event !== 'message') return;
        this.messageEmitter.emit('message', event.data);
      },
    });
    const reader = body.getReader();
    const decoder = new TextDecoder();
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        parser.feed(decoder.decode(value, { stream: true }));
      }
      // 서버가 스트림 종료
      if (this.gracefullyEnded) {
        // 정상 완료 sentinel(`end`)을 받고 닫힘 → 재연결하지 않고 clean-close.
        // disconnect(true): refCount 무시 즉시 'closed' → share()가 map에서 제거(좀비 재사용 방지).
        this.disconnect(true);
      } else if (!controller.signal.aborted) {
        // sentinel 없이 끊김(네트워크 등) → 에러로 처리해 AutoReconnect가 재연결
        this.error = new Error('SSE stream ended');
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        this.error = err as Error;
      }
    }
  }

  override doDisconnect(conn: AbortController): void {
    conn.abort();
  }
}
